import { FormEvent, useEffect, useRef, useState } from "react"
import { t } from "../../i18n"
import { SageEmployee } from "../../Interfaces"
import { SageApiError } from "../../utils/sageApiError"
import { isWholeSageNumber, parseWholeSageNumber } from "../../utils/sageCodes"
import { sageEmployeeService } from "../../services/sageEmployeeService"
import CwFormModal, { CwField, cwInputClassName } from "./CwFormModal"

export default function EmployeeFormModal({
  isOpen,
  companyId,
  employee,
  onClose,
  onSaved,
}: {
  isOpen: boolean
  companyId: number
  employee?: SageEmployee | null
  onClose: () => void
  onSaved: () => void
}) {
  const editing = Boolean(employee?.id)
  const [employeeNum, setEmployeeNum] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [employeeNumError, setEmployeeNumError] = useState("")
  const [firstNameError, setFirstNameError] = useState("")
  const [lastNameError, setLastNameError] = useState("")
  const [formError, setFormError] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const numberRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isOpen) return
    setEmployeeNum(employee?.employeeNum != null ? String(employee.employeeNum) : "")
    setFirstName(employee?.firstName || "")
    setLastName(employee?.lastName || "")
    setEmployeeNumError("")
    setFirstNameError("")
    setLastNameError("")
    setFormError("")
    setSubmitting(false)
    const id = window.setTimeout(() => numberRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [isOpen, employee])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const trimmedFirst = firstName.trim()
    const trimmedLast = lastName.trim()
    const nextNumError = !employeeNum.trim()
      ? t("employees.employeeNumRequired")
      : isWholeSageNumber(employeeNum)
        ? ""
        : t("employees.employeeNumInvalid")
    const nextFirstError = trimmedFirst ? "" : t("employees.firstNameRequired")
    const nextLastError = trimmedLast ? "" : t("employees.lastNameRequired")
    setEmployeeNumError(nextNumError)
    setFirstNameError(nextFirstError)
    setLastNameError(nextLastError)
    setFormError("")
    const parsedNum = parseWholeSageNumber(employeeNum)
    if (nextNumError || nextFirstError || nextLastError || parsedNum == null) return

    setSubmitting(true)
    try {
      const body = { employeeNum: parsedNum, firstName: trimmedFirst, lastName: trimmedLast }
      if (editing && employee) {
        await sageEmployeeService.update(companyId, employee.id, body)
      } else {
        await sageEmployeeService.create(companyId, body)
      }
      onSaved()
      onClose()
    } catch (error) {
      setFormError(error instanceof SageApiError && error.message
        ? error.message
        : t("employees.loadFailed"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <CwFormModal
      isOpen={isOpen}
      title={editing ? t("employees.modalEditTitle") : t("employees.modalAddTitle")}
      submitLabel={t("common.save")}
      submitting={submitting}
      formError={formError}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <CwField
        id="employee-num"
        label={t("employees.employeeNumLabel")}
        helper={t("employees.employeeNumHelper")}
        error={employeeNumError}
      >
        <input
          ref={numberRef}
          id="employee-num"
          name="employeeNum"
          inputMode="numeric"
          value={employeeNum}
          disabled={submitting}
          onChange={(event) => {
            setEmployeeNum(event.target.value)
            if (employeeNumError) setEmployeeNumError("")
          }}
          className={cwInputClassName}
        />
      </CwField>
      <CwField id="employee-first-name" label={t("employees.firstNameLabel")} error={firstNameError}>
        <input
          id="employee-first-name"
          name="firstName"
          value={firstName}
          disabled={submitting}
          onChange={(event) => {
            setFirstName(event.target.value)
            if (firstNameError) setFirstNameError("")
          }}
          className={cwInputClassName}
        />
      </CwField>
      <CwField id="employee-last-name" label={t("employees.lastNameLabel")} error={lastNameError}>
        <input
          id="employee-last-name"
          name="lastName"
          value={lastName}
          disabled={submitting}
          onChange={(event) => {
            setLastName(event.target.value)
            if (lastNameError) setLastNameError("")
          }}
          className={cwInputClassName}
        />
      </CwField>
    </CwFormModal>
  )
}
